import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { Resource, Scope } from "../../../../../../types/NendaTypes";
import PermissionsGate from "../../../components/PermissionGate";
import { t } from "i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffectNotOnMount } from "../../../../../hooks/useEffectNotOnMount";
import { MdLocalMovies } from "react-icons/md";
import { Form, Formik, Field } from "formik";
import { Modal } from "../../../../Modal/Modal";
import { BasicTable } from "../../../../Table/BasicTable";
import * as Yup from "yup";
import { store } from "../../../store";
import {
  CreateAssetAction,
  GetAssetsAction,
  getPaginatorSelector,
  getVODSelector,
  getVODsSelector,
} from "../../../store/reducers/cmsReducer";
import CMSPageHeader from "../../../ui-components/pageLayout/CMSPageHeader";
import { ModalWrapper } from "../../../ui-components/pageLayout/ModalComponents";
import { AssetsPageTableSettings } from "./VODSTableSettings";
import { CMSAsset } from "../../../../../models/CMS/cmsAsset";
import { Select, MenuItem } from "@mui/material";
import VODFilter from "./VODFilter";
import VODActiveFilters from "./VODActiveFilters";
import {
  EMPTY_FILTERS,
  VodListParams,
  VODFilters,
  buildVodAssetsQuery,
  parseVodListParams,
  toVodListSearchParams,
} from "./vodListParams";

interface CreateAssetValues {
  name: string;
  type: string;
}

const ASSET_TYPE_OPTIONS = [
  { value: "movie", label: "Movie" },
  { value: "series", label: "Series" },
  { value: "season", label: "Season" },
  { value: "episode", label: "Episode" },
];

const CreateAssetSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required*"),
  type: Yup.string()
    .oneOf(["movie", "series", "season", "episode"])
    .required("Required*"),
});

const VODsPage: React.FC = (): ReactNode => {
  const [showForm, setShowForm] = React.useState(false);
  const assets = useSelector(getVODsSelector);
  const pagination = useSelector(getPaginatorSelector);
  const asset = useSelector(getVODSelector);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [filteredAssets, setFilteredAssets] = useState<CMSAsset[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const history = useNavigate();

  const searchKey = searchParams.toString();
  const listParams = useMemo(
    () => parseVodListParams(new URLSearchParams(searchKey)),
    [searchKey]
  );
  const { q: search, filters, page, pageSize } = listParams;

  useEffect(() => {
    store.dispatch(GetAssetsAction(buildVodAssetsQuery(listParams)));
  }, [listParams]);

  useEffect(() => {
    // Filter out channel-type assets
    const vodAssets = assets.filter(
      (asset) =>
        asset.getType() !== "channel" && asset.getType() !== "liveChannel"
    );
    setFilteredAssets(vodAssets);
  }, [assets]);

  function submitCreate(values: CreateAssetValues) {
    setIsCreating(true);
    store.dispatch(CreateAssetAction(values));
  }

  const updateListParams = (
    changes: Partial<VodListParams>,
    options: { replace?: boolean } = {}
  ) => {
    setSearchParams(toVodListSearchParams({ ...listParams, ...changes }), {
      replace: options.replace,
    });
  };

  const handlePagination = (nextPage: number) => {
    updateListParams({ page: nextPage }, { replace: true });
  };

  const handleRowsPerPage = (rowsPerPage: number) => {
    updateListParams(
      { page: 1, pageSize: Number(rowsPerPage) },
      { replace: true }
    );
  };

  const handleSearch = (nextSearch: string) => {
    updateListParams({ q: nextSearch.trim(), page: 1 });
  };

  const handleFiltersChange = (newFilters: VODFilters) => {
    updateListParams({ filters: newFilters, page: 1 });
  };

  const handleClearAll = () => {
    updateListParams({ q: "", filters: EMPTY_FILTERS, page: 1 });
  };

  const getRowLink = (row: CMSAsset) => ({
    to: `/admin/metadata/vod/${row.getUniqueId()}`,
    state: { listSearch: location.search },
  });

  useEffectNotOnMount(() => {
    if (asset && isCreating) {
      redirect(asset.getUniqueId());
    }
  }, [asset]);

  const redirect = (id) => {
    history(`/admin/metadata/vod/${id}`, {
      state: { listSearch: location.search },
    });
  };

  return (
    <PermissionsGate
      restriction={{
        resource: Resource.Content,
        scopes: [Scope.CanAdministrate],
      }}
    >
      <>
        <CMSPageHeader
          title={t("customerportal.pages.vod.title")}
          count={pagination.total}
          button={{
            title: t("customerportal.pages.vod.create"),
            onClick: () => setShowForm(true),
            icon: MdLocalMovies,
          }}
          onSearch={handleSearch}
          searchValue={search}
          filterSlot={
            <VODFilter filters={filters} onChange={handleFiltersChange} />
          }
        />
        <VODActiveFilters
          search={search}
          filters={filters}
          onSearchChange={handleSearch}
          onFiltersChange={handleFiltersChange}
          onClearAll={handleClearAll}
        />
        <Formik
          initialValues={{
            name: "",
            type: "movie",
          }}
          validationSchema={CreateAssetSchema}
          onSubmit={(values: CreateAssetValues) => {
            submitCreate(values);
            setShowForm(false);
          }}
        >
          {({ handleSubmit, values, setFieldValue }) => (
            <Form>
              <ModalWrapper>
                <Modal
                  visible={showForm}
                  onClose={() => setShowForm(false)}
                  title="Create new asset"
                  confirmText="Create"
                  onSubmit={handleSubmit}
                  closeOnSubmit={false}
                >
                  <div className="input-group my-1">
                    <div className="input-group-prepend">
                      <span className="input-group-text">Asset name</span>
                    </div>
                    <Field
                      name="name"
                      placeholder="Asset Name..."
                      className="form-control"
                    />
                  </div>
                  <div className="input-group my-1">
                    <div className="input-group-prepend">
                      <span className="input-group-text">Asset type</span>
                    </div>
                    <Select
                      onChange={(e) => setFieldValue("type", e.target.value)}
                      value={values.type}
                      className="form-control"
                    >
                      {ASSET_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </Modal>
              </ModalWrapper>
            </Form>
          )}
        </Formik>
        <BasicTable
          tableSettings={AssetsPageTableSettings}
          tableData={filteredAssets || []}
          getRowLink={getRowLink}
          paginationControls={{
            paginationCount: pagination.total,
            handlePagination: handlePagination,
            handleRowsPerPage: handleRowsPerPage,
            currentPage: page,
            currentRowsPerPage: pageSize,
          }}
        />
      </>
    </PermissionsGate>
  );
};

export default VODsPage;
